// M004-F0748 (WebUI #188, design section 8.1). The READ-ONLY claim opened from a queue row: receipts (the confirmed fields; the picture and zoom are
// DEFERRED to Core #402 - no claim-scoped image route exists, so no image is requested), totals, history, and - for a submitted claim only - Return all (reason required, allowed on one's own claim) and Approve all
// (disabled with its reason on one's own claim; a Ledger refusal is shown with its code). Whole claim only, no per-receipt action.
// Dialog contract (Q4): focus moves in, Tab stays inside, Escape closes the drawer, focus returns to the row.
import React, { useEffect, useRef, useState } from 'react';
import { claimReviewApi } from '../../services/claimReviewApi';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import useDialogFocus from '../envelopes/useDialogFocus';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import {
  approveDecision, cleanReason, formatDay, money, refusalInfo, returnDecision, stateText,
} from './claimReviewModel';

const amountText = (amount) => (amount ? money(amount.minor, amount.currency) : 'Not confirmed');

const NO_IMAGE = 'Image preview not available yet';

// Features: M004-F0775 (WebUI #248). An unknown outcome is never shown as a failure: the Ledger may have applied the approve or return.
const UNCONFIRMED = {
  approve: "We couldn't confirm whether this claim was approved. We've refreshed what the Ledger shows — check it before trying again.",
  return: "We couldn't confirm whether this claim was returned. We've refreshed what the Ledger shows — check it before trying again.",
};

function Fields({ receipt }) {
  const rows = [
    ['Merchant', receipt.merchant], ['Date', receipt.receipt_date ? formatDay(receipt.receipt_date) : ''], ['Amount', amountText(receipt.amount)],
    ['VAT', receipt.vat_minor === undefined || receipt.vat_minor === null ? '' : money(receipt.vat_minor, receipt.amount?.currency)], ['Reference', receipt.reference], ['Note', receipt.note],
  ].filter(([, value]) => value);
  return <dl className="cr-fields">{rows.map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}</dl>;
}

export default function ClaimReviewDrawer({ orgId, summary, onClose, onDone }) {
  const transactionId = summary.transaction_id;
  const ref = useRef(null);
  const reasonRef = useRef(null);
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [stages, setStages] = useState([]);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const [failure, setFailure] = useState(null);
  const [actionUnconfirmed, setActionUnconfirmed] = useState(''); // Features: M004-F0775
  const [reloads, setReloads] = useState(0); // bumped after an unknown outcome: the claim view re-reads itself through the same effect
  // WebUI #274 (Ledger #284): approve may need SAL 4. Core answers 401 STEP_UP_REQUIRED with a challenge; the dialog opens and completing it retries approve once.
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  useDialogFocus(ref, onClose, { initialFocus: '[data-testid="claim-drawer-close"]' });

  useEffect(() => {
    let current = true;
    setState({ status: 'loading', data: null, error: null });
    claimReviewApi.review(orgId, transactionId)
      .then((response) => {
        const data = response?.data?.data;
        if (!current) return;
        if (!data?.claim) setState({ status: 'error', data: null, error: { code: '', message: 'The Ledger sent no claim.' } });
        else setState({ status: 'ready', data, error: null });
      })
      .catch((cause) => { if (current) setState({ status: 'error', data: null, error: refusalInfo(cause) }); });
    claimReviewApi.history(orgId, transactionId)
      .then((response) => { if (current && Array.isArray(response?.data?.data?.stages)) setStages(response.data.data.stages); })
      .catch(() => { /* history is a courtesy; the claim itself is what is reviewed */ });
    return () => { current = false; };
  }, [orgId, transactionId, reloads]);

  useEffect(() => { if (returning) reasonRef.current?.focus(); }, [returning]);

  const claim = { ...summary, ...(state.data?.claim || {}), own_claim: summary.own_claim };
  const approve = approveDecision(claim);
  const giveBack = returnDecision(claim, reason);

  // `retry` is the one re-send after a completed step-up: a second step-up answer is then shown as a refusal, not a second dialog.
  const act = async (kind, retry = false) => {
    if (busy) return;
    setBusy(kind);
    setFailure(null);
    setActionUnconfirmed('');
    try {
      const response = kind === 'return'
        ? await claimReviewApi.returnClaim(orgId, summary.envelope_id, cleanReason(reason))
        : await claimReviewApi.approve(orgId, summary.envelope_id);
      const view = response?.data?.data;
      onDone({ kind, transactionId: view?.transaction_id || transactionId, name: summary.envelope_name, state: view?.state });
    } catch (cause) {
      setBusy('');
      const challenge = kind === 'approve' && !retry ? getStepUpChallengeFromError(cause) : null;
      if (challenge?.id) {
        // Step-up, not a refusal and not an unknown outcome: nothing was written. Cancel clears busy and shows nothing.
        setStepUpChallenge(challenge);
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        // Features: M004-F0775 - no refusal text; re-read the claim the view already reads.
        setActionUnconfirmed(UNCONFIRMED[kind]);
        setReloads((n) => n + 1);
      } else {
        setFailure(refusalInfo(cause));
      }
    }
  };

  const receipts = state.data?.receipts || [];
  const envelopeTotals = state.data?.envelope?.totals || [];
  const unconfirmed = envelopeTotals[0]?.unconfirmed || 0;

  return (
    <>
    <StepUpDialog challenge={stepUpChallenge} title="Verify claim approval" description="Confirm your identity before approving this claim."
      onComplete={() => { setStepUpChallenge(null); act('approve', true); }}
      onCancel={() => setStepUpChallenge(null)} />
    <div className="cr-backdrop" role="presentation" onClick={onClose}>
      <aside ref={ref} className="cr-drawer" role="dialog" aria-modal="true" aria-labelledby="cr-drawer-title" data-testid="claim-drawer" onClick={(event) => event.stopPropagation()}>
        <header className="cr-drawer-head">
          <div>
            <h2 id="cr-drawer-title">{summary.envelope_name} - {summary.claimant_label}</h2>
            <p className="cr-muted" data-testid="claim-drawer-txn">{transactionId} <span className={`cr-pill cr-state-${claim.state}`} data-testid="claim-drawer-state">{stateText(claim.state)}</span> {formatDay(summary.submitted_at)}</p>
          </div>
          <button type="button" className="cr-btn" data-testid="claim-drawer-close" onClick={onClose}>Close</button>
        </header>

        {state.status === 'loading' ? <p className="cr-muted" role="status">Opening the claim...</p> : null}
        {state.status === 'error' ? <p className="cr-error" role="alert" data-testid="claim-drawer-error">{state.error.message}{state.error.code ? <> <code>{state.error.code}</code></> : null}</p> : null}

        {state.status === 'ready' ? (
          <>
            <p className="cr-totals" data-testid="claim-drawer-totals">
              {receipts.length} {receipts.length === 1 ? 'receipt' : 'receipts'} - {money(claim.total_minor, claim.currency)} (VAT {money(claim.vat_minor, claim.currency)})
            </p>
            {unconfirmed ? <p className="cr-notice" data-testid="claim-drawer-unconfirmed">{unconfirmed} receipt{unconfirmed === 1 ? ' has' : 's have'} no confirmed amount.</p> : null}
            {claim.state === 'returned' && claim.reason ? <p className="cr-notice" data-testid="claim-drawer-reason">Returned: {claim.reason}</p> : null}
            <ul className="cr-receipts" data-testid="claim-receipts" aria-label="Receipts in this claim (read only)">
              {receipts.map((receipt) => (
                <li key={receipt.receipt_document_id} data-testid={`claim-receipt-${receipt.receipt_document_id}`}>
                  <div>
                    <Fields receipt={receipt} />
                    <p className="cr-muted cr-noimage" data-testid={`claim-receipt-noimage-${receipt.receipt_document_id}`}>{NO_IMAGE}</p>
                  </div>
                </li>
              ))}
            </ul>
            {stages.length ? (
              <section aria-label="History">
                <h3 className="cr-h3">History</h3>
                <ol className="cr-history" data-testid="claim-history">
                  {stages.map((stage) => <li key={stage.seq}>{stage.stage}{stage.reason ? `: ${stage.reason}` : ''} <span className="cr-muted">{formatDay(stage.at)}</span></li>)}
                </ol>
              </section>
            ) : null}
          </>
        ) : null}

        {failure ? (
          <p className="cr-error" role="alert" data-testid="claim-action-error">{failure.message} <code data-testid="claim-action-error-code">{failure.code}</code></p>
        ) : null}
        {actionUnconfirmed ? <p className="cr-notice" role="status" data-testid="claim-action-unconfirmed">{actionUnconfirmed}</p> : null}

        {state.status === 'ready' && claim.state === 'submitted' ? (
          <footer className="cr-actions">
            {returning ? (
              <div className="cr-reason">
                <label htmlFor="cr-reason-input">Reason for returning (required)</label>
                <textarea id="cr-reason-input" ref={reasonRef} rows={3} value={reason} data-testid="claim-return-reason" aria-describedby="cr-reason-hint" onChange={(event) => setReason(event.target.value)} />
                <p id="cr-reason-hint" className="cr-muted" data-testid="claim-return-hint">{giveBack.disabled ? giveBack.reason : 'The claimant sees this reason and can edit and resubmit.'}</p>
                <div className="cr-row">
                  <button type="button" className="cr-btn cr-btn-danger" data-testid="claim-return-confirm" disabled={giveBack.disabled || Boolean(busy)} onClick={() => act('return')}>Return</button>
                  <button type="button" className="cr-btn" data-testid="claim-return-cancel" onClick={() => { setReturning(false); setReason(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="cr-row">
                <button type="button" className="cr-btn" data-testid="claim-return" disabled={Boolean(busy)} onClick={() => setReturning(true)}>Return all...</button>
                <button type="button" className="cr-btn cr-btn-primary" data-testid="claim-approve" disabled={approve.disabled || Boolean(busy)} aria-describedby={approve.disabled ? 'cr-approve-why' : undefined} onClick={() => act('approve')}>Approve all</button>
              </div>
            )}
            {approve.disabled && !returning ? <p id="cr-approve-why" className="cr-muted" data-testid="claim-approve-why">{approve.reason}</p> : null}
          </footer>
        ) : null}
      </aside>
    </div>
    </>
  );
}
